/* eslint-disable unicorn/prefer-number-properties */
/* eslint-disable jsdoc/require-returns-description */
/* eslint-disable react-refresh/only-export-components */
import { createClient } from '@supabase/supabase-js'
import {
  IconAlertTriangleFilled,
  IconBarcode,
  IconBox,
  IconBoxOff,
  IconCalendarEvent,
  IconCalendarPin,
  IconCalendarTime,
  IconCheck,
  IconChecks,
  IconCircleCheckFilled,
  IconCircleXFilled,
  IconClockCheck,
  IconClockExclamation,
  IconCubePlus,
  IconDeviceMobile,
  IconHeart,
  IconHourglassHigh,
  IconMapPin,
  IconMapPinFilled,
  IconPlaneTilt,
  IconProgressCheck,
  IconReplace,
  IconRoute,
  IconShip,
  IconTag,
  IconTemperature,
  IconTruck,
  IconTruckDelivery,
  IconTruckLoading,
  IconUsers,
  IconUserSearch,
  IconVaccineBottle,
  IconZoomCheck,
} from '@tabler/icons-react'
import moment from 'moment'
import * as React from 'react'
import atriskIcon from '../assets/at-risk.svg'
import calenderMonthIcon from '../assets/calendar-month.svg'
import CubeTick from '../assets/cube-tick'
import offtrackIcon from '../assets/off-track.svg'
import ontrackIcon from '../assets/on-track.svg'
import ontrackIconGray from '../assets/ontrack-gray.svg'
import TemperatureDeviationIcon from '../assets/temperature-deviation.svg'
import TimeZones from '../components/Header/timezones.json'
import timezones from '../data/timezones.json'
import { clearAllTagsCatalog } from '../services/tagsCatalog'
import { resetActiveUserOrgStore } from '../stores/activeUserOrgStore'
import { resetUserFeatureStore, setUserFeatureDefinitions } from '../stores/userFeatureStore'
import { resolveEdgeFromApiVersions, resolveRpcFromApiVersions } from './apiVersionMatrix'
import { formatAppDateTime } from './appDateTimeFormat'
import { getUserPreferencesGlobal } from './globalPreferences'
import { isProductReleaseV1Enabled } from './productReleaseStatsigGateConstants'
import { isProductReleasePathPermissionCheckEnabled } from './releaseStatsigGateConstants'
import {
  getShipmentTempReleaseReviewersApiName,
  getShipmentUagUserIdsRpcName,
} from './shipmentListTagPivot'

import { getTenantIdFromContext, resolveContxtBackendApiKey } from './tenantBackendApiKey'

// Singleton instances with session tracking
const clients = {
  liveRisk: { client: null, lastSessionId: null },
  commandCenter: { client: null, lastSessionId: null },
  commandCenterEdge: { client: null, lastSessionId: null },
  product: { client: null, lastSessionId: null },
  landingZone: { client: null, lastSessionId: null },
  landingZoneEdge: { client: null, lastSessionId: null },
  parserEdge: { client: null, lastSessionId: null },
  realtimeLiveRisk: { client: null, lastSessionId: null },
  realtimeCommandCenter: { client: null, lastSessionId: null },
  parser: { client: null, lastSessionId: null },
  tempRelease: { client: null, lastSessionId: null },
  realtimeTempRelease: { client: null, lastSessionId: null },
  tempReleaseEdge: { client: null, lastSessionId: null },
  workflows: { client: null, lastSessionId: null },
  realtimeWorkflows: { client: null, lastSessionId: null },
  realtimeLandingZone: { client: null, lastSessionId: null },
  commandCenterEdgeExternal: { client: null },
  insights: { client: null, lastSessionId: null },
}

const getSessionId = (session) => {
  return session?.id || 'anonymous'
}

export const createClerkSupabaseClientLiveRisk = (session) => {
  const sessionId = getSessionId(session)

  if (!clients.liveRisk.client || clients.liveRisk.lastSessionId !== sessionId) {
    clients.liveRisk.client = createClient(
      import.meta.env.VITE_SUPABASE_LIVE_RISK_URL,
      import.meta.env.VITE_SUPABASE_LIVE_RISK_KEY,
      {
        global: {
          fetch: async (url, options = {}) => {
            const clerkToken = (await session?.getToken()) ?? null
            const headers = new Headers(options?.headers)
            headers.set('Authorization', `Bearer ${clerkToken}`)
            return fetch(url, {
              ...options,
              headers,
            })
          },
        },
      },
    )
    clients.liveRisk.lastSessionId = sessionId
  }
  return clients.liveRisk.client
}

export const createRealtimeSupabaseClientLiveRisk = (session) => {
  const sessionId = getSessionId(session)

  if (!clients.realtimeLiveRisk.client || clients.realtimeLiveRisk.lastSessionId !== sessionId) {
    clients.realtimeLiveRisk.client = createClient(
      import.meta.env.VITE_SUPABASE_LIVE_RISK_URL,
      import.meta.env.VITE_SUPABASE_LIVE_RISK_KEY,
      {
        accessToken: async () =>
          (await session?.getToken?.({
            template: import.meta.env.VITE_CLERK_REALTIME_JWT_TEMPLATE,
          })) ?? null,
      },
    )
    clients.realtimeLiveRisk.lastSessionId = sessionId
  }
  return clients.realtimeLiveRisk.client
}

export const createClerkSupabaseClientCommandCenter = (session) => {
  const sessionId = getSessionId(session)

  if (!clients.commandCenter.client || clients.commandCenter.lastSessionId !== sessionId) {
    clients.commandCenter.client = createClient(
      import.meta.env.VITE_SUPABASE_COMMAND_CENTER_URL,
      import.meta.env.VITE_SUPABASE_COMMAND_CENTER_KEY,
      /* When accessToken is provided, Supabase uses that token directly for API/realtime auth and does not spin up the full internal auth flow for that client.
            So we avoid unnecessary duplicate GoTrue clients for Command Center. */
      {
        accessToken: async () => (await session?.getToken?.()) ?? null,
      },
    )
    clients.commandCenter.lastSessionId = sessionId
  }
  return clients.commandCenter.client
}

export function getInsightsSupabaseConfigError() {
  const url = import.meta.env.VITE_SUPABASE_INSIGHTS_URL || ''
  const key = import.meta.env.VITE_SUPABASE_INSIGHTS_KEY || ''
  const missing = []
  if (!url) missing.push('VITE_SUPABASE_INSIGHTS_URL')
  if (!key) missing.push('VITE_SUPABASE_INSIGHTS_KEY')
  if (!missing.length) return null
  return `Missing ${missing.join(' and ')} in .env. Add the Insights project URL/key, then restart the dev server.`
}

/** Insights / Lightdash Supabase project (hosts `lightdash-ai-summary`). */
export const createClerkSupabaseClientInsights = (session) => {
  const url = import.meta.env.VITE_SUPABASE_INSIGHTS_URL || ''
  const key = import.meta.env.VITE_SUPABASE_INSIGHTS_KEY || ''
  if (!url || !key) return null

  const sessionId = getSessionId(session)
  if (!clients.insights.client || clients.insights.lastSessionId !== sessionId) {
    clients.insights.client = createClient(url, key, {
      accessToken: async () => (await session?.getToken?.()) ?? null,
    })
    clients.insights.lastSessionId = sessionId
  }
  return clients.insights.client
}

export const createClerkSupabaseClientCommandCenterEdgeExternal = () => {
  if (!clients.commandCenterEdgeExternal.client) {
    const anonKey = import.meta.env.VITE_SUPABASE_COMMAND_CENTER_KEY
    clients.commandCenterEdgeExternal.client = createClient(
      import.meta.env.VITE_SUPABASE_COMMAND_CENTER_URL,
      anonKey,
      /* When accessToken is provided, Supabase uses that token directly for API/realtime auth and does not spin up the full internal auth flow for that client.
            So we avoid unnecessary duplicate GoTrue clients for Command Center. */
      {
        accessToken: async () => anonKey,
      },
    )
  }
  return clients.commandCenterEdgeExternal.client
}

export const createRealtimeSupabaseClientCommandCenter = (session) => {
  const sessionId = getSessionId(session)

  if (
    !clients.realtimeCommandCenter.client ||
    clients.realtimeCommandCenter.lastSessionId !== sessionId
  ) {
    clients.realtimeCommandCenter.client = createClient(
      import.meta.env.VITE_SUPABASE_COMMAND_CENTER_URL,
      import.meta.env.VITE_SUPABASE_COMMAND_CENTER_KEY,
      {
        accessToken: async () =>
          (await session?.getToken?.({
            template: import.meta.env.VITE_CLERK_REALTIME_JWT_TEMPLATE,
          })) ?? null,
      },
    )
    clients.realtimeCommandCenter.lastSessionId = sessionId
  }
  return clients.realtimeCommandCenter.client
}

export const removeCCSubscriptions = (session) => {
  if (!session) return
  const client = createRealtimeSupabaseClientCommandCenter(session)
  client.getChannels()?.forEach((channel) => {
    const topic = channel?.topic || ''
    if (topic.includes('shipments/') || topic.includes('shipment_temperature_releases/')) {
      channel.unsubscribe()
      client.removeChannel(channel)
    }
  })
}

export const createClerkSupabaseClientProduct = (session) => {
  const sessionId = getSessionId(session)

  if (!clients.product.client || clients.product.lastSessionId !== sessionId) {
    clients.product.client = createClient(
      import.meta.env.VITE_SUPABASE_PRODUCT_URL,
      import.meta.env.VITE_SUPABASE_PRODUCT_KEY,
      {
        global: {
          fetch: async (url, options = {}) => {
            const clerkToken = (await session?.getToken()) ?? null
            const headers = new Headers(options?.headers)
            headers.set('Authorization', `Bearer ${clerkToken}`)
            return fetch(url, {
              ...options,
              headers,
            })
          },
        },
      },
    )
    clients.product.lastSessionId = sessionId
  }
  return clients.product.client
}

export const createClerkSupabaseClientLandingZone = (session, customHeader) => {
  const sessionId = getSessionId(session)

  if (!clients.landingZone.client || clients.landingZone.lastSessionId !== sessionId) {
    clients.landingZone.client = createClient(
      import.meta.env.VITE_SUPABASE_LANDING_ZONE_URL,
      import.meta.env.VITE_SUPABASE_LANDING_ZONE_KEY,
      {
        global: {
          fetch: async (url, options = {}) => {
            const clerkToken = (await session?.getToken()) ?? null
            const headers = new Headers(options?.headers)
            headers.set('Authorization', `Bearer ${clerkToken}`)
            if (customHeader) {
              Object.keys(customHeader).forEach((key) => {
                headers.set(key, customHeader[key])
              })
            }
            return fetch(url, {
              ...options,
              headers,
            })
          },
        },
      },
    )
    clients.landingZone.lastSessionId = sessionId
  }
  return clients.landingZone.client
}

export const createRealtimeSupabaseClientLandingZone = (session) => {
  const sessionId = getSessionId(session)

  if (
    !clients.realtimeLandingZone.client ||
    clients.realtimeLandingZone.lastSessionId !== sessionId
  ) {
    clients.realtimeLandingZone.client = createClient(
      import.meta.env.VITE_SUPABASE_LANDING_ZONE_URL,
      import.meta.env.VITE_SUPABASE_LANDING_ZONE_KEY,
      {
        accessToken: async () =>
          (await session?.getToken?.({
            template: import.meta.env.VITE_CLERK_REALTIME_JWT_TEMPLATE,
          })) ?? null,
      },
    )
    clients.realtimeLandingZone.lastSessionId = sessionId
  }
  return clients.realtimeLandingZone.client
}

export const createClerkSupabaseClientLandingZoneEdge = (session, customHeader) => {
  const sessionId = getSessionId(session)

  if (!clients.landingZoneEdge.client || clients.landingZoneEdge.lastSessionId !== sessionId) {
    clients.landingZoneEdge.client = createClient(
      import.meta.env.VITE_SUPABASE_LANDING_ZONE_URL,
      import.meta.env.VITE_SUPABASE_LANDING_ZONE_KEY,
      {
        global: {
          fetch: async (url, options = {}) => {
            // const clerkToken = await session?.getToken() ?? null;
            const headers = new Headers(options?.headers)
            headers.set('Authorization', `Bearer ${import.meta.env.VITE_SUPABASE_LANDING_ZONE_KEY}`)
            if (customHeader) {
              Object.keys(customHeader).forEach((key) => {
                headers.set(key, customHeader[key])
              })
            }
            return fetch(url, {
              ...options,
              headers,
            })
          },
        },
      },
    )
    clients.landingZoneEdge.lastSessionId = sessionId
  }
  return clients.landingZoneEdge.client
}

export const createClerkSupabaseClientParser = (session) => {
  const sessionId = getSessionId(session)

  if (!clients.parser.client || clients.parser.lastSessionId !== sessionId) {
    clients.parser.client = createClient(
      import.meta.env.VITE_SUPABASE_PARSER_URL,
      import.meta.env.VITE_SUPABASE_PARSER_KEY,
      {
        global: {
          fetch: async (url, options = {}) => {
            const clerkToken = (await session?.getToken()) ?? null
            const headers = new Headers(options?.headers)
            headers.set('Authorization', `Bearer ${clerkToken}`)
            return fetch(url, {
              ...options,
              headers,
            })
          },
        },
      },
    )
    clients.parser.lastSessionId = sessionId
  }
  return clients.parser.client
}

export const createClerkSupabaseClientParserEdge = (session) => {
  const sessionId = getSessionId(session)

  if (!clients.parserEdge.client || clients.parserEdge.lastSessionId !== sessionId) {
    clients.parserEdge.client = createClient(
      import.meta.env.VITE_SUPABASE_PARSER_URL,
      import.meta.env.VITE_SUPABASE_PARSER_KEY,
      {
        global: {
          fetch: async (url, options = {}) => {
            const headers = new Headers(options?.headers)
            headers.set('Authorization', `Bearer ${import.meta.env.VITE_SUPABASE_PARSER_KEY}`)
            return fetch(url, {
              ...options,
              headers,
            })
          },
        },
      },
    )
    clients.parserEdge.lastSessionId = sessionId
  }
  return clients.parserEdge.client
}

export const createClerkSupabaseClientTempRelease = (session) => {
  const sessionId = getSessionId(session)

  if (!clients.tempRelease.client || clients.tempRelease.lastSessionId !== sessionId) {
    clients.tempRelease.client = createClient(
      import.meta.env.VITE_SUPABASE_TEMP_RELEASE_URL,
      import.meta.env.VITE_SUPABASE_TEMP_RELEASE_KEY,
      {
        global: {
          fetch: async (url, options = {}) => {
            const clerkToken = (await session?.getToken()) ?? null
            const headers = new Headers(options?.headers)
            headers.set('Authorization', `Bearer ${clerkToken}`)
            return fetch(url, {
              ...options,
              headers,
            })
          },
        },
      },
    )
    clients.tempRelease.lastSessionId = sessionId
  }
  return clients.tempRelease.client
}

export const createClerkSupabaseClientTempReleaseEdge = (session) => {
  const sessionId = getSessionId(session)

  if (!clients.tempReleaseEdge.client || clients.tempReleaseEdge.lastSessionId !== sessionId) {
    clients.tempReleaseEdge.client = createClient(
      import.meta.env.VITE_SUPABASE_TEMP_RELEASE_URL,
      import.meta.env.VITE_SUPABASE_TEMP_RELEASE_KEY,
      {
        global: {
          fetch: async (url, options = {}) => {
            const headers = new Headers(options?.headers)
            headers.set('Authorization', `Bearer ${import.meta.env.VITE_SUPABASE_TEMP_RELEASE_KEY}`)
            return fetch(url, {
              ...options,
              headers,
            })
          },
        },
      },
    )
    clients.tempReleaseEdge.lastSessionId = sessionId
  }
  return clients.tempReleaseEdge.client
}

export const createRealtimeSupabaseClientTempRelease = (session) => {
  const sessionId = getSessionId(session)

  if (
    !clients.realtimeTempRelease.client ||
    clients.realtimeTempRelease.lastSessionId !== sessionId
  ) {
    clients.realtimeTempRelease.client = createClient(
      import.meta.env.VITE_SUPABASE_TEMP_RELEASE_URL,
      import.meta.env.VITE_SUPABASE_TEMP_RELEASE_KEY,
      {
        accessToken: async () =>
          (await session?.getToken?.({
            template: import.meta.env.VITE_CLERK_REALTIME_JWT_TEMPLATE,
          })) ?? null,
      },
    )
    clients.realtimeTempRelease.lastSessionId = sessionId
  }
  return clients.realtimeTempRelease.client
}

export const createClerkSupabaseClientWorkflows = (session, additional_header = null) => {
  const sessionId = getSessionId(session)

  if (!clients.workflows.client || clients.workflows.lastSessionId !== sessionId) {
    clients.workflows.client = createClient(
      import.meta.env.VITE_SUPABASE_WORKFLOWS_URL,
      import.meta.env.VITE_SUPABASE_WORKFLOWS_KEY,
      {
        global: {
          fetch: async (url, options = {}) => {
            const clerkToken = (await session?.getToken()) ?? null
            const headers = new Headers(options?.headers)
            headers.set('Authorization', `Bearer ${clerkToken}`)
            if (additional_header) {
              headers.set('AuthorizationToken', `Bearer ${clerkToken}`)
            }
            return fetch(url, {
              ...options,
              headers,
            })
          },
        },
      },
    )
    clients.workflows.lastSessionId = sessionId
  }
  return clients.workflows.client
}

export const createRealtimeSupabaseClientWorkflows = (session) => {
  const sessionId = getSessionId(session)

  if (!clients.realtimeWorkflows.client || clients.realtimeWorkflows.lastSessionId !== sessionId) {
    clients.realtimeWorkflows.client = createClient(
      import.meta.env.VITE_SUPABASE_WORKFLOWS_URL,
      import.meta.env.VITE_SUPABASE_WORKFLOWS_KEY,
      {
        accessToken: async () =>
          (await session?.getToken?.({
            template: import.meta.env.VITE_CLERK_REALTIME_JWT_TEMPLATE,
          })) ?? null,
      },
    )
    clients.realtimeWorkflows.lastSessionId = sessionId
  }
  return clients.realtimeWorkflows.client
}

export const getFormattedDuration = (MS, returnString) => {
  // console.log('================: ', ms);
  if (MS === 'NA' || MS === null || MS === undefined || Number.isNaN(MS)) return 'NA'
  const ms = Math.abs(MS)
  const diff = moment.duration(ms)._data
  // console.log('ms: ', ms, diff, t, diff.seconds);
  let str = ''
  if (diff.years > 0) {
    str = `${diff.years} year${diff.years !== 1 ? 's' : ''} ${
      diff.months > 0 ? `and ${diff.months} month${diff.months !== 1 ? 's' : ''}` : ''
    }`
  } else if (diff.months > 0) {
    str = `${diff.months} month${diff.months !== 1 ? 's' : ''} ${
      diff.days > 0 ? `and ${diff.days} day${diff.days !== 1 ? 's' : ''}` : ''
    }`
  } else if (diff.days > 0) {
    str = `${diff.days} day${diff.days !== 1 ? 's' : ''} ${
      diff.hours > 0
        ? `and ${diff.hours + (diff.minutes > 30 ? 1 : 0)} hour${diff.hours !== 1 ? 's' : ''}`
        : ''
    }`
  } else if (diff.hours > 0) {
    str = `${diff.hours} hour${diff.hours !== 1 ? 's' : ''} ${
      diff.minutes > 0 ? `and ${diff.minutes} min${diff.minutes !== 1 ? '' : ''}` : ''
    }`
  } else if (diff.minutes > 0) {
    str = `${diff.minutes} min${diff.minutes !== 1 ? '' : ''}`
  } else {
    str = `${diff.seconds > 0 ? '1 min' : '0 min'}`
  }

  if (returnString) return str
  return (
    <span
      title={`${diff.years > 0 ? `${diff.years}year${diff.years === 1 ? '' : 's'}` : ''}${
        diff.months > 0 ? ` ${diff.months}month${diff.months === 1 ? '' : 's'}` : ''
      }${diff.days > 0 ? ` ${diff.days}day${diff.days === 1 ? '' : 's'}` : ''}${
        diff.hours > 0 ? ` ${diff.hours}hour${diff.hours === 1 ? '' : 's'}` : ''
      }${diff.minutes > 0 ? ` ${diff.minutes}minute${diff.minutes === 1 ? '' : 's'}` : ''}${
        diff.seconds > 0 ? ` ${diff.seconds}second${diff.seconds === 1 ? '' : 's'}` : ''
      }`}
    >
      {str}
    </span>
  )
}

export const getRangeLabel = (range_min, range_max) => {
  if ((range_min === 0 || range_min) && (range_max === 0 || range_max) && range_min === range_max) {
    return `[= ${range_min}]`
  } else if ((range_min === 0 || range_min) && (range_max === 0 || range_max)) {
    return `[${range_min} to ${range_max}]`
  } else if ((range_min === null || range_min === undefined) && (range_max === 0 || range_max)) {
    return `[< ${range_max}]`
  } else if ((range_min === 0 || range_min) && (range_max === null || range_max === undefined)) {
    return `[> ${range_min}]`
  } else {
    return ``
  }
}

export const getExcludeRangeLabel = (range_min, range_max) => {
  if ((range_min === 0 || range_min) && (range_max === 0 || range_max) && range_min === range_max) {
    return `= ${range_min}`
  } else if ((range_min === 0 || range_min) && (range_max === 0 || range_max)) {
    return `${range_min} to ${range_max}`
  } else if ((range_min === null || range_min === undefined) && (range_max === 0 || range_max)) {
    return `${import.meta.env.VITE_RANGE_MAX_LABEL || 'Above'} ${range_max}`
  } else if ((range_min === 0 || range_min) && (range_max === null || range_max === undefined)) {
    return `${import.meta.env.VITE_RANGE_MIN_LABEL || 'Below'} ${range_min}`
  } else {
    return ``
  }
}

export const fetchOrganizationUsersV3 = async (session, params = {}) => {
  const {
    organization_id,
    query,
    limit,
    offset,
    order_by,
    only_invited,
    only_active,
    only_suspended,
    without_suspended_users,
    no_user_access_groups,
    user_access_group_ids,
    sort_by,
    sort_order,
    include_all,
  } = params

  if (!session || !organization_id) return

  const clerkToken = (await session?.getToken()) ?? null

  const queryParams = new URLSearchParams()
  queryParams.set('organization_id', organization_id)
  queryParams.set('env', import.meta.env.VITE_ENVIRONMENT)
  if (query) queryParams.set('query', query)
  if (limit !== undefined && limit !== null) queryParams.set('limit', String(limit))
  if (offset !== undefined && offset !== null) queryParams.set('offset', String(offset))
  if (order_by) queryParams.set('order_by', order_by)
  if (only_invited) queryParams.set('only_invited', 'true')
  if (only_active) queryParams.set('only_active', 'true')
  if (only_suspended) queryParams.set('only_suspended', 'true')
  if (without_suspended_users) queryParams.set('without_suspended_users', 'true')
  if (no_user_access_groups) queryParams.set('no_user_access_groups', 'true')
  if (sort_by) queryParams.set('sort_by', sort_by)
  if (sort_order) queryParams.set('sort_order', sort_order)
  if (include_all) queryParams.set('include_all', 'true')
  if (Array.isArray(user_access_group_ids) && user_access_group_ids.length > 0) {
    queryParams.set('user_access_group_ids', user_access_group_ids.join(','))
  } else if (typeof user_access_group_ids === 'string' && user_access_group_ids) {
    queryParams.set('user_access_group_ids', user_access_group_ids)
  }

  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_COMMAND_CENTER_URL}/functions/v1/get-organization-users-v3?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${clerkToken}`,
        },
      },
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      return {
        success: false,
        message: errorData.message || `Error: ${response.status}`,
      }
    }

    const data = await response.json()
    return {
      success: true,
      message: 'Users fetched successfully',
      data,
    }
  } catch (error) {
    console.error('Error fetching organization users:', error)
    return { success: false, message: error.message }
  }
}

export const getAllowedShipmentUsers = async (session, shipment_uuid, statsigClient) => {
  if (!session || !shipment_uuid) return []

  try {
    const cc_client = createClerkSupabaseClientCommandCenter(session)
    const { data, error } = await cc_client.rpc(getShipmentUagUserIdsRpcName(statsigClient), {
      p_shipment_uuid: shipment_uuid,
    })

    if (error) {
      console.error('Error fetching shipment UAG user ids:', error)
      return []
    }

    return data || []
  } catch (err) {
    console.error('Error fetching shipment UAG user ids:', err)
    return []
  }
}

const collapseSubmittedReviewersByUser = (rows) => {
  const reviewersByUserId = new Map()
  const sortedRows = [...rows].sort((left, right) => (left?.sort_key ?? 0) - (right?.sort_key ?? 0))

  for (const row of sortedRows) {
    const userId = row?.user_id
    if (!userId || reviewersByUserId.has(userId)) continue
    reviewersByUserId.set(userId, row)
  }

  return [...reviewersByUserId.values()]
}

export const getSubmittedReviewersData = async (
  session,
  shipment_uuid,
  { uniqueByUser = false } = {},
) => {
  if (!session || !shipment_uuid) return []

  try {
    const cc_client = createClerkSupabaseClientCommandCenter(session)
    const { data, error } = await cc_client
      .from('temperature_reviews')
      .select('*')
      .eq('shipment_uuid', shipment_uuid)

    if (error) {
      console.error('Error fetching submitted reviewers:', error)
      return []
    }

    const rows = data || []
    if (!uniqueByUser || rows.length === 0) return rows
    return collapseSubmittedReviewersByUser(rows)
  } catch (err) {
    console.error('Error fetching submitted reviewers:', err)
    return []
  }
}

const normalizeProductProfileUuids = (productProfileUuid) => {
  const values = Array.isArray(productProfileUuid) ? productProfileUuid : [productProfileUuid]
  return values.map((value) => String(value || '').trim()).filter(Boolean)
}

export const getUserSumiittedReviewedData = async (
  session,
  {
    shipmentUuid,
    handlingUnitId,
    userId,
    checkKey = 'temperature_review',
    orgId,
    productProfileUuid,
  } = {},
) => {
  const productProfileUuids = normalizeProductProfileUuids(productProfileUuid)

  if (
    !session ||
    !shipmentUuid ||
    !handlingUnitId ||
    !userId ||
    !checkKey ||
    !orgId ||
    productProfileUuids.length === 0
  ) {
    return []
  }

  try {
    const cc_client = createClerkSupabaseClientCommandCenter(session)
    let request = cc_client
      .from('temperature_reviews')
      .select('*')
      .eq('shipment_uuid', shipmentUuid)
      .eq('handling_unit_id', handlingUnitId)
      .eq('user_id', userId)
      .eq('check_key', checkKey)
      .eq('org_id', orgId)

    request =
      productProfileUuids.length === 1
        ? request.eq('product_profile_uuid', productProfileUuids[0])
        : request.in('product_profile_uuid', productProfileUuids)

    const { data, error } = await request

    if (error) {
      console.error('Error fetching submitted reviewers:', error)
      return []
    }

    return data || []
  } catch (err) {
    console.error('Error fetching submitted reviewers:', err)
    return []
  }
}

export { fetchorgnizationUAGName } from '../components/ManageOrganization/UserGroupUtility'

const mapClerkUserToTempReleaseReviewer = (clerkUser) => {
  const userId = clerkUser?.user_id || clerkUser?.id
  const firstName = clerkUser?.firstName || clerkUser?.first_name || ''
  const lastName = clerkUser?.lastName || clerkUser?.last_name || ''
  const imageUrl =
    clerkUser?.imageUrl ||
    clerkUser?.image_url ||
    clerkUser?.profile_image_url ||
    clerkUser?.profile_photo ||
    ''

  return {
    user_id: userId,
    first_name: firstName,
    last_name: lastName,
    username: clerkUser?.username || '',
    image_url: imageUrl,
    profile_image_url: imageUrl,
  }
}

export const getClerkUserInfoById = async (session, userIds) => {
  if (!userIds || userIds.length === 0) return []

  try {
    const clerkToken = (await session?.getToken()) ?? null
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_COMMAND_CENTER_URL}/functions/v1/get-clerk-user-info-by-id`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${clerkToken}`,
        },
        body: JSON.stringify({ userId: userIds }),
      },
    )

    if (!response.ok) {
      console.error(`Error fetching clerk users: ${response.status}`)
      return []
    }

    const data = await response.json()
    return data?.users || data?.data || []
  } catch (error) {
    console.error('Exception while fetching clerk user details:', error)
    return []
  }
}

export const fetchShipmentTempReleaseReviewers = async (
  session,
  shipment_id,
  org_id,
  statsigClient,
) => {
  if (!session || !shipment_id || !org_id) return []
  const clerkToken = (await session?.getToken()) ?? null
  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_COMMAND_CENTER_URL}/functions/v1/${resolveEdgeFromApiVersions(getShipmentTempReleaseReviewersApiName(statsigClient))}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${clerkToken}`,
        },
        body: JSON.stringify({
          shipment_id,
          org_id,
          without_suspended_users: true,
        }),
      },
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error(
        'Error fetching shipment temprelease reviewers:',
        errorData?.message || response.status,
      )
      return []
    }

    const data = await response.json()
    const userIds = data?.user_ids || []

    if (!Array.isArray(userIds) || userIds.length === 0) {
      return []
    }

    const clerkUsers = await getClerkUserInfoById(session, userIds)
    const reviewersById = new Map()

    ;(clerkUsers || []).forEach((clerkUser) => {
      const mappedReviewer = mapClerkUserToTempReleaseReviewer(clerkUser)
      if (mappedReviewer.user_id) {
        reviewersById.set(mappedReviewer.user_id, mappedReviewer)
      }
    })

    return userIds
      .map(
        (userId) =>
          reviewersById.get(userId) || {
            user_id: userId,
            first_name: '',
            last_name: '',
            username: userId,
            image_url: '',
            profile_image_url: '',
          },
      )
      .filter((reviewer) => reviewer.user_id)
  } catch (error) {
    console.error('Error fetching shipment temprelease reviewers:', error)
    return []
  }
}

export const getOrganizationFeatureList = async (cc_client, organizationId) => {
  if (!organizationId) return

  try {
    const query = await cc_client
      .from('default_organization_features')
      .select(`features`)
      .eq('org_id', organizationId)

    const { data, error } = await query

    if (error) {
      console.error('Error fetching user access group features: ', error)
      return { success: false, message: error.message }
    } else {
      return data
    }
  } catch (error) {
    console.error('Error fetching organization users:', error)
    return { success: false, message: error.message }
  }
}

export const getOrganizationUserAccessGroupList = async (
  cc_client,
  organizationId,
  userId,
  callUagAssignments = false,
) => {
  if (!userId || !organizationId) return

  try {
    let query = null
    if (callUagAssignments) {
      query = cc_client
        .from('uag_assignments')
        .select('topic_key')
        .eq('org_id', organizationId)
        .eq('deleted', 'FALSE')
        .eq('clerk_user_id', userId)
    } else {
      query = cc_client
        .from('user_access_groups')
        .select('features')
        .eq('org_id', organizationId)
        .eq('deleted', 'FALSE')
        .contains('subscribers', [userId])
    }

    const { data, error } = await query

    if (error) {
      console.error('Error fetching user access groups: ', error)
      return { success: false, message: error.message }
    } else {
      return data
    }
  } catch (error) {
    console.error('Error fetching users access groups:', error)
    return { success: false, message: error.message }
  }
}

export const getUserDynamicConfig = async ({ cc_client, organizationId, callBack }) => {
  if (!organizationId || !cc_client) {
    if (callBack) callBack({})
    return {}
  }

  try {
    const { data, error } = await cc_client
      .from('default_organization_features')
      .select('org_config')
      .eq('org_id', organizationId)
      .single()

    if (error) {
      console.error('Error fetching user dynamic config:', error)
      if (callBack) callBack(data)
      return {}
    }

    if (callBack) callBack(data)
    return data
  } catch (err) {
    console.error('Error fetching user dynamic config:', err)
    if (callBack) callBack({})
    return {}
  }
}

const getGroupSortOrder = (group) => {
  const sortOrders = (group?.features || [])
    .map((feature) => feature?.sort_order)
    .filter((order) => typeof order === 'number')

  if (!sortOrders.length) return Number.MAX_SAFE_INTEGER
  return Math.min(...sortOrders)
}

const PERMISSION_ACTIONS = ['read', 'manage', 'can_review']

const getPermissionEntry = (userPermissions, featureKey) => {
  if (!userPermissions || !featureKey) return undefined
  if (Object.prototype.hasOwnProperty.call(userPermissions, featureKey)) {
    return userPermissions[featureKey]
  }
  return userPermissions[featureKey]
}

const hasActiveUserPermission = (userPermissions, featureKey) => {
  const perms = getPermissionEntry(userPermissions, featureKey)
  if (!perms) return false
  return PERMISSION_ACTIONS.some((action) => perms[action])
}

export const getFirstPermittedFeatureUrl = (userPermissions = {}, featureDefinitions = []) => {
  const permittedFeatures = (featureDefinitions || [])
    .flatMap((group) => group?.features || [])
    .filter((feature) => feature?.url && hasActiveUserPermission(userPermissions, feature?.key))
    .sort(
      (a, b) =>
        (a?.sort_order ?? Number.MAX_SAFE_INTEGER) - (b?.sort_order ?? Number.MAX_SAFE_INTEGER),
    )

  return permittedFeatures[0]?.url || null
}

const createDefaultUagFeatureConfig = (data) => {
  const permittedFeatureDefinitions = data?.feature_definitions
    ?.map((group) => {
      const features = (group?.features || []).filter((sf) =>
        hasActiveUserPermission(data?.user_permissions, sf?.key),
      )
      if (!features.length) return null
      return { ...group, features }
    })
    ?.filter(Boolean)

  const uagFeatures = permittedFeatureDefinitions
    ?.filter((f) =>
      f?.features?.some(
        (sf) => sf?.bucket === 'configurable_module' || sf?.bucket === 'uag_feature',
      ),
    )
    ?.slice()
    ?.sort((a, b) => getGroupSortOrder(a) - getGroupSortOrder(b))

  const defaultUagFeatureConfig = uagFeatures?.map((f) => {
    const sortedFeatures = [...(f?.features || [])].sort(
      (a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0),
    )

    return {
      ...f,
      features: sortedFeatures,
      is_group: sortedFeatures.length > 1 || !!f.subtitle,
    }
  })

  return defaultUagFeatureConfig
}

export const createDefaultUagFeatureConfigFromOrgFeatures = (data) => {
  const orgFeatures = {}
  data?.org_features.forEach((f) => {
    orgFeatures[f.feature_key] = f.value
  })

  const permittedFeatureDefinitions = JSON.parse(localStorage?.defaultUagFeatureConfig)
    ?.map((group) => {
      const features = (group?.features || []).filter((sf) =>
        orgFeatures.hasOwnProperty(sf.key) ? orgFeatures[sf.key] : sf?.bucket === 'uag_feature',
      )
      if (!features.length) return null
      return { ...group, features }
    })
    ?.filter(Boolean)

  const uagFeatures = permittedFeatureDefinitions
    ?.filter((f) =>
      f?.features?.some(
        (sf) => sf?.bucket === 'configurable_module' || sf?.bucket === 'uag_feature',
      ),
    )
    ?.slice()
    ?.sort((a, b) => getGroupSortOrder(a) - getGroupSortOrder(b))

  const defaultUagFeatureConfig = uagFeatures?.map((f) => {
    const sortedFeatures = [...(f?.features || [])].sort(
      (a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0),
    )

    return {
      ...f,
      features: sortedFeatures,
      is_group: sortedFeatures.length > 1 || !!f.subtitle,
    }
  })

  return defaultUagFeatureConfig
}

const createDefaultOrgConfig = (data) => {
  const orgFeaturePermissions = {}
  const isOrgOwner = data?.is_org_owner || false
  let defaultOrgFeatureConfig = data?.feature_definitions
    ?.filter((f) => f?.features?.some((sf) => sf?.bucket === 'org_setting'))
    ?.slice()
    ?.sort((a, b) => getGroupSortOrder(a) - getGroupSortOrder(b))

  if (isOrgOwner) {
    defaultOrgFeatureConfig = defaultOrgFeatureConfig?.filter((f) =>
      f.features.some((sf) => sf.configured_by !== 'tenant_owner'),
    )
  }

  data?.feature_definitions
    ?.filter((f) => f?.features?.some((sf) => sf?.bucket === 'org_setting'))
    ?.slice()
    .forEach((f) => {
      f.features.forEach((sf) => {
        orgFeaturePermissions[sf.key] =
          data?.org_features?.find((of) => of.feature_key === sf.key)?.value || false
      })
    })

  return { defaultOrgFeatureConfig, orgFeaturePermissions }
}

const createTenantFeaturePermissions = (data) => {
  const tenantFeaturePermissions = data?.tenant_features?.reduce((acc, f) => {
    acc[f.feature_key] = f.value
    return acc
  }, {})
  return tenantFeaturePermissions || {}
}

const storeFeaturesPermissionsData = (data) => {
  localStorage.setItem('userUagName', JSON.stringify(data?.uag_name || ''))
  localStorage.setItem('userUagId', JSON.stringify(data?.uag_id || ''))
  localStorage.setItem('isTenantOwner', JSON.stringify(data?.is_tenant_owner || false))
  localStorage.setItem('userFeaturePermissions', JSON.stringify(data?.user_permissions || []))
  localStorage.setItem(
    'userFeaturePermissionsTags',
    JSON.stringify(data?.user_permission_tags || []),
  )
  setUserFeatureDefinitions(data?.feature_definitions || [])
  const defaultUagFeatureConfig = createDefaultUagFeatureConfig(data)
  localStorage.setItem('defaultUagFeatureConfig', JSON.stringify(defaultUagFeatureConfig))
  const { defaultOrgFeatureConfig, orgFeaturePermissions } = createDefaultOrgConfig(data)
  localStorage.setItem('defaultOrgFeatureConfig', JSON.stringify(defaultOrgFeatureConfig))
  localStorage.setItem('orgFeaturePermissions', JSON.stringify(orgFeaturePermissions))
  localStorage.setItem('backendOnlyFeaturePermissions', JSON.stringify(data?.backend_features))
  const tenantFeaturePermissions = createTenantFeaturePermissions(data)
  localStorage.setItem('tenantFeaturePermissions', JSON.stringify(tenantFeaturePermissions))
}

export const getUserFeaturePermissionsV2 = async ({
  cc_client,
  callBack,
  storeData = false,
  statsigClient,
}) => {
  if (!cc_client) {
    if (callBack) callBack({})
    return {}
  }

  const rpcName = isProductReleaseV1Enabled(statsigClient)
    ? 'get_user_features_v3'
    : 'get_user_features_v2'

  try {
    const { data, error } = await cc_client.rpc(rpcName)

    if (error) {
      console.error(`Error fetching ${rpcName}:`, error)
      if (callBack) callBack({})
      return {}
    }

    if (storeData) storeFeaturesPermissionsData(data)
    if (callBack) callBack(data)
    return {
      user_permissions: data?.user_permissions || {},
      feature_definitions: data?.feature_definitions || [],
      tenant_features: data?.tenant_features || [],
    }
  } catch (err) {
    console.error(`Error fetching ${rpcName}:`, err)
    if (callBack) callBack({})
    return {}
  }
}

/**
 * Keeps org setting values in the type the General screen uses to pick a control.
 * Booleans stay toggles. Strings stay single selects. Arrays stay multi selects.
 * @param {unknown} rawValue
 */
function resolveOrgFeatureValue(rawValue) {
  if (Array.isArray(rawValue)) return rawValue
  if (
    typeof rawValue === 'boolean' ||
    typeof rawValue === 'string' ||
    typeof rawValue === 'number'
  ) {
    return rawValue
  }
  if (rawValue && typeof rawValue === 'object') {
    return resolveOrgFeatureValue(rawValue.value ?? rawValue.enabled ?? rawValue.is_enabled)
  }
  return Boolean(rawValue)
}

/**
 * Normalizes org feature rows from `get_org_features` into `{ feature_key, value }`.
 * @param {unknown} orgFeatures
 */
export function normalizeOrgFeatureRows(orgFeatures) {
  if (!orgFeatures) return []

  if (Array.isArray(orgFeatures)) {
    return orgFeatures
      .map((item) => {
        if (!item || typeof item !== 'object') return null

        const feature_key = item.feature_key || item.key || item.featureKey
        if (!feature_key) return null

        const rawValue = item.value ?? item.enabled ?? item.is_enabled
        return {
          feature_key,
          value: resolveOrgFeatureValue(rawValue),
        }
      })
      .filter(Boolean)
  }

  if (typeof orgFeatures === 'object') {
    return Object.entries(orgFeatures).map(([feature_key, rawValue]) => ({
      feature_key,
      value: resolveOrgFeatureValue(rawValue),
    }))
  }

  return []
}

/**
 * Normalizes `get_org_features` RPC payloads into org feature state used by org settings.
 * @param {unknown} data
 */
export function normalizeOrgFeaturesResponse(data) {
  if (!data) {
    return { org_features: [], feature_definitions: [] }
  }

  let payload = data
  if (
    payload &&
    typeof payload === 'object' &&
    !Array.isArray(payload) &&
    payload.data &&
    typeof payload.data === 'object'
  ) {
    payload = payload.data
  }

  if (Array.isArray(payload)) {
    return { org_features: normalizeOrgFeatureRows(payload), feature_definitions: [] }
  }

  const orgFeatures = normalizeOrgFeatureRows(
    payload.org_features ?? payload.features ?? payload.orgFeatures,
  )
  const featureDefinitions = payload.feature_definitions ?? payload.featureDefinitions ?? []

  return {
    org_features: orgFeatures,
    feature_definitions: Array.isArray(featureDefinitions) ? featureDefinitions : [],
  }
}

function featureDefinitionKey(feature) {
  return feature?.key || feature?.feature_key || ''
}

function indexFeaturesByKey(definitions = []) {
  const featuresByKey = new Map()
  ;(definitions || []).forEach((group) => {
    ;(group?.features || []).forEach((feature) => {
      const key = featureDefinitionKey(feature)
      if (key) featuresByKey.set(key, feature)
    })
  })
  return featuresByKey
}

function definitionsNeedAllowedValues(orgFeatures, featureDefinitions) {
  const featuresByKey = indexFeaturesByKey(featureDefinitions)
  return (orgFeatures || []).some((item) => {
    const needsOptions = typeof item?.value === 'string' || Array.isArray(item?.value)
    if (!needsOptions) return false
    const feature = featuresByKey.get(item.feature_key)
    return !Array.isArray(feature?.allowed_values) || feature.allowed_values.length === 0
  })
}

function mergeAllowedValuesIntoDefinitions(featureDefinitions, catalogDefinitions) {
  const catalogByKey = indexFeaturesByKey(catalogDefinitions)
  return (featureDefinitions || []).map((group) => ({
    ...group,
    features: (group?.features || []).map((feature) => {
      if (Array.isArray(feature?.allowed_values) && feature.allowed_values.length > 0) {
        return feature
      }
      const catalogFeature = catalogByKey.get(featureDefinitionKey(feature))
      if (
        !Array.isArray(catalogFeature?.allowed_values) ||
        catalogFeature.allowed_values.length === 0
      ) {
        return feature
      }
      return {
        ...feature,
        allowed_values: catalogFeature.allowed_values,
      }
    }),
  }))
}

/**
 * Fetch organization-scoped features via `get_org_features(p_org_id)`.
 * Used when a tenant owner manages an org they are not actively switched into.
 * @param {object} params
 * @param {import('@supabase/supabase-js').SupabaseClient} params.cc_client
 * @param {string} params.organizationId
 * @param {(data: object) => void} [params.callBack]
 * @param {object | null | undefined} [params.statsigClient]
 */
export const getOrgFeatures = async ({ cc_client, organizationId, callBack, statsigClient }) => {
  if (!cc_client || !organizationId) {
    if (callBack) callBack({ org_features: [], feature_definitions: [] })
    return { org_features: [], feature_definitions: [] }
  }

  try {
    const { data, error } = await cc_client.rpc(resolveRpcFromApiVersions('get_org_features'), {
      p_org_id: organizationId,
    })

    if (error) {
      console.error('Error fetching org features:', error)
      if (callBack) callBack({ org_features: [], feature_definitions: [] })
      return { org_features: [], feature_definitions: [] }
    }

    let normalized = normalizeOrgFeaturesResponse(data)
    const needsDefinitions = normalized.feature_definitions.length === 0
    const needsAllowedValues = definitionsNeedAllowedValues(
      normalized.org_features,
      normalized.feature_definitions,
    )

    if (needsDefinitions || needsAllowedValues) {
      const definitionsResult = await getUserFeaturePermissionsV2({ cc_client, statsigClient })
      const catalogDefinitions = definitionsResult?.feature_definitions || []
      normalized = {
        ...normalized,
        feature_definitions: needsDefinitions
          ? catalogDefinitions
          : mergeAllowedValuesIntoDefinitions(normalized.feature_definitions, catalogDefinitions),
      }
    }

    if (callBack) callBack(normalized)
    return normalized
  } catch (err) {
    console.error('Error fetching org features:', err)
    if (callBack) callBack({ org_features: [], feature_definitions: [] })
    return { org_features: [], feature_definitions: [] }
  }
}

export const getUserFeaturePermissions = async ({ cc_client, organizationId, user, callBack }) => {
  if (!organizationId || !user?.id || !cc_client) {
    if (callBack) callBack({})
    return {}
  }

  try {
    const { data, error } = await cc_client.rpc(
      resolveRpcFromApiVersions('get_user_org_features'),
      {
        p_user_id: user.id,
        p_org_id: organizationId,
      },
    )

    if (error) {
      console.error('Error fetching user org features:', error)
      if (callBack) callBack({})
      return {}
    }

    let featurePermissions = data
    if (featurePermissions == null) {
      featurePermissions = {}
    } else if (typeof featurePermissions === 'string') {
      try {
        featurePermissions = JSON.parse(featurePermissions)
      } catch {
        featurePermissions = {}
      }
    }
    if (typeof featurePermissions !== 'object' || Array.isArray(featurePermissions)) {
      featurePermissions = {}
    }

    if (callBack) callBack(featurePermissions)
    return featurePermissions
  } catch (err) {
    console.error('Error fetching user org features:', err)
    if (callBack) callBack({})
    return {}
  }
}

export const roundOffValue = (val) => {
  return Number.isInteger(val) ? val : Number.parseFloat(val).toFixed(0)
}

export const getDate = (
  t,
  returnDate,
  dontConvert,
  dontUseDefaultDate,
  onlyDate,
  showSeconds,
  monthAsString,
  dateFormat,
) => {
  let d = t
  if (t && t.toString().includes('GMT')) {
    /* empty */
  } else if (t) {
    d = t.toString().includes('-') ? t.replace(/-/g, '/') : t
  }

  const T = d
    ? new Date(
        new Date(Number.isNaN(d) ? d : Number(d)).getTime() -
          (dontConvert ? 0 : new Date().getTimezoneOffset() * 60 * 1000),
      )
    : dontUseDefaultDate
      ? 0
      : new Date()

  if (Number.isNaN(new Date(T).getTime()) || T === 0) {
    return '--'
  }
  if (returnDate) {
    return T
  }
  // console.log('======================\n',t, '\n',T);
  const day = T.getDate()
  const month = T.getMonth()
  const year = T.getFullYear()
  const today = new Date()
  let connectingString = 'at'
  // console.log(T.getTime(),' | ', today.getTime());
  if (T.getTime() > today.getTime()) {
    connectingString = 'by'
  }

  if (onlyDate) {
    return moment(T).format('MM/DD/YYYY')
  }
  if (day === today.getDate() && month === today.getMonth() && year === today.getFullYear()) {
    return `Today ${connectingString} ${moment(T).format(`HH:mm${showSeconds ? ':ss' : ''}`)}`
  }
  const format = dateFormat || `MM/DD/YYYY at HH:mm${showSeconds ? ':ss' : ''}`
  let date = moment(T)
    .format(format)
    .replace(/amt|pmt/, connectingString)
  if (monthAsString) {
    // Format date with month as string directly using moment
    const monthFormat = format.replace(/MM|mm/, 'MMM')
    date = moment(T)
      .format(monthFormat)
      .replace(/amt|pmt/, connectingString)
  }
  return date
}

export const getFormattedDurationShort = (
  MS,
  toolTip = false,
  returnString = true,
  showSeconds = false,
) => {
  if (MS === 'NA' || MS === null || MS === undefined || Number.isNaN(MS)) return 'NA'
  const ms = Math.abs(MS)
  // const diff = moment.duration(ms)._data;
  const diff = {
    years: 0,
    months: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  }
  const TIME_OBJ = {
    year: 12 * 30 * 24 * 60 * 60 * 1000,
    month: 30 * 24 * 60 * 60 * 1000,
    day: 24 * 60 * 60 * 1000,
    hour: 60 * 60 * 1000,
    minute: 60 * 1000,
    second: 1000,
    millisecond: 1,
  }
  diff.years = Math.floor(ms / TIME_OBJ.year)
  diff.months = Math.floor((ms % TIME_OBJ.year) / TIME_OBJ.month)
  diff.days = Math.floor(((ms % TIME_OBJ.year) % TIME_OBJ.month) / TIME_OBJ.day)
  diff.hours = Math.floor((((ms % TIME_OBJ.year) % TIME_OBJ.month) % TIME_OBJ.day) / TIME_OBJ.hour)
  diff.minutes = Math.floor(
    ((((ms % TIME_OBJ.year) % TIME_OBJ.month) % TIME_OBJ.day) % TIME_OBJ.hour) / TIME_OBJ.minute,
  )
  diff.seconds = Math.floor(
    (((((ms % TIME_OBJ.year) % TIME_OBJ.month) % TIME_OBJ.day) % TIME_OBJ.hour) % TIME_OBJ.minute) /
      TIME_OBJ.second,
  )
  diff.milliseconds = Math.floor(
    ((((((ms % TIME_OBJ.year) % TIME_OBJ.month) % TIME_OBJ.day) % TIME_OBJ.hour) %
      TIME_OBJ.minute) %
      TIME_OBJ.second) /
      TIME_OBJ.millisecond,
  )
  // console.log('ms: ', ms, diff, diff.seconds);
  let str = ''
  if (diff.years > 0) {
    str = `${diff.years}y${diff.months > 0 ? ` ${diff.months}m` : ''}`
  } else if (diff.months > 0) {
    str = `${diff.months}m${diff.days > 0 ? ` ${diff.days}d` : ''}`
  } else if (diff.days > 0) {
    str = `${diff.days}d${diff.hours > 0 ? ` ${diff.hours}hr` : ''}${
      diff.minutes > 0 ? ` ${diff.minutes}min` : ''
    }`
  } else if (diff.hours > 0) {
    str = `${diff.hours}hr${diff.minutes > 0 ? ` ${diff.minutes}min` : ''}`
  } else if (diff.minutes > 0) {
    str = `${diff.minutes}min${showSeconds && diff.seconds > 0 ? ` ${diff.seconds}s` : ''}`
  } else if (showSeconds) {
    str = `${diff.seconds > 0 ? diff.seconds : diff.milliseconds > 0 ? 1 : 0}s`
  } else {
    str = `${diff.seconds > 0 || diff.milliseconds > 0 ? '1min' : '0min'}`
  }
  // console.log('==================|', ms, '<><><>|',str,'|');
  if (returnString) {
    return str
  } else if (toolTip) {
    return (
      <span className="t-tip t" style={{ color: 'var(--Text-large-08)' }}>
        {str}
        <span className="t-txt">
          {diff.years > 0 ? `${diff.years}y` : ''}
          {diff.months > 0 ? ` ${diff.months}m` : ''}
          {diff.days > 0 ? ` ${diff.days}d` : ''}
          {diff.hours > 0 ? ` ${diff.hours}h` : ''}
          {diff.minutes > 0 ? ` ${diff.minutes}min` : ''}
          {diff.seconds > 0 ? ` ${diff.seconds}s` : ''}
        </span>
      </span>
    )
  } else {
    return (
      <span
        title={`${diff.years > 0 ? `${diff.years}year${diff.years === 1 ? '' : 's'}` : ''}${
          diff.months > 0 ? ` ${diff.months}month${diff.months === 1 ? '' : 's'}` : ''
        }${diff.days > 0 ? ` ${diff.days}day${diff.days === 1 ? '' : 's'}` : ''}${
          diff.hours > 0 ? ` ${diff.hours}hour${diff.hours === 1 ? '' : 's'}` : ''
        }${diff.minutes > 0 ? ` ${diff.minutes}minute${diff.minutes === 1 ? '' : 's'}` : ''}${
          diff.seconds > 0 ? ` ${diff.seconds}second${diff.seconds === 1 ? '' : 's'}` : ''
        }`}
      >
        {str}
      </span>
    )
  }
}

export const getFormattedDate = (tms, includeAt = true, includeSeconds = false, useUTC) => {
  try {
    // Create a Date object from epoch milliseconds
    const timestamp = Number(tms) || Date.now()
    const date = new Date(timestamp)
    const globalUserPreferences = getUserPreferencesGlobal()

    let timeZone = useUTC ? 'UTC' : globalUserPreferences.timezone || 'UTC'
    if (!useUTC) {
      const { pathname } = window.location
      if (pathname.includes('external/track-')) {
        timeZone = Intl.DateTimeFormat()?.resolvedOptions()?.timeZone
      }
    }

    // Shared format with a cached formatter per time zone (appDateTimeFormat.js)
    return formatAppDateTime(date, { timeZone, includeAt, includeSeconds })
  } catch (error) {
    console.error('Error formatting date:', error)
    return 'Invalid date'
  }
}

export const truncateMiddle = (text, returnString) => {
  if (!text) return ''
  if (text.length <= 15) return text

  const start = text.slice(0, 8)
  const end = text.slice(-6)
  if (returnString) {
    return `<span className="col-start-1">${start}</span><span className="col-start-2 text-Text-small-04">...</span><span className="col-start-3">${end}</span>`
  }
  return (
    <>
      <span className="col-start-1">{start}</span>
      <span className="col-start-2 text-Text-small-04">...</span>
      <span className="col-start-3">{end}</span>
    </>
  )
}

export const epochToUtcFormat = (timestamp, includeMicroseconds = false) => {
  try {
    const date = new Date(Number(timestamp))

    if (Number.isNaN(date.getTime())) {
      return null
    }

    // toISOString() gives: "2025-06-03T14:07:03.156Z"
    const isoString = date.toISOString()

    // Split into parts: date + time and milliseconds
    const [dateTimePart, millisecondsPart] = isoString.replace('Z', '').split('.')

    // Convert milliseconds to microseconds (pad to 6 digits)
    const microseconds = includeMicroseconds ? (millisecondsPart || '000').padEnd(6, '0') : ''

    // Format: "2025-06-03 14:07:03.156000+00"
    return `${dateTimePart.replace('T', ' ') + (microseconds ? `.${microseconds}` : '')}+00`
  } catch (error) {
    console.error('Error converting epoch to UTC format:', error)
    return null
  }
}

/**
 * @param {object} [options]
 * @param {boolean} [options.preserveOrgCommandCenterViews] Keep per-org command-center view maps (session + localStorage) during org switch.
 * @param {boolean} [options.preserveTagsCatalog] Skip wiping tags catalog memory/IndexedDB (same-org impersonation).
 */
export const clearLocalStorage = ({
  preserveOrgCommandCenterViews = false,
  preserveTagsCatalog = false,
} = {}) => {
  resetUserFeatureStore()
  resetActiveUserOrgStore()
  if (!preserveTagsCatalog) {
    void clearAllTagsCatalog()
  }

  let preservedOrgCommandCenterViews = null
  let preservedActiveCommandCenterOrgId = null
  if (preserveOrgCommandCenterViews && typeof localStorage !== 'undefined') {
    try {
      preservedOrgCommandCenterViews = localStorage.getItem('command-center-views-by-org')
      preservedActiveCommandCenterOrgId = localStorage.getItem('command-center-active-org-id')
    } catch {
      preservedOrgCommandCenterViews = null
      preservedActiveCommandCenterOrgId = null
    }
  }

  localStorage.clear()

  if (preserveOrgCommandCenterViews && typeof localStorage !== 'undefined') {
    try {
      if (preservedOrgCommandCenterViews) {
        localStorage.setItem('command-center-views-by-org', preservedOrgCommandCenterViews)
      }
      if (preservedActiveCommandCenterOrgId) {
        localStorage.setItem('command-center-active-org-id', preservedActiveCommandCenterOrgId)
      }
    } catch {
      // ignore private-mode / quota errors during org switch hygiene
    }
  }

  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem('command-center-persisted-system-view')
      if (!preserveOrgCommandCenterViews) {
        sessionStorage.removeItem('command-center-views-by-org')
      }
    } catch {
      // ignore private-mode / quota errors during sign-out hygiene
    }
  }
}

export const getQuarter = () => {
  const currentDate = new Date()
  const currentMonth = currentDate.getMonth()
  const quarter = Math.floor(currentMonth / 3) + 1
  return `Q${quarter}`
}

/**
 * Fetches the list of active devices from device_mapping table for use in filters.
 * @param {object} commandCenterClient - Supabase client for command center (createClerkSupabaseClientCommandCenter(session))
 * @returns {Promise<Array<{ filterTitle: string, id: string|number, count: number }>>}
 */
export const fetchDeviceMappingList = async (commandCenterClient) => {
  if (!commandCenterClient) return []
  try {
    const { data, error } = await commandCenterClient
      .from('device_mapping')
      .select('device_id, device_type')
      .eq('device_type', 'active')
      .order('updated_at', { ascending: false })
    if (error) {
      console.error('Error fetching device_mapping for filters:', error)
      return []
    }
    return (data || []).map((row) => ({
      filterTitle: row.device_id ?? 'Unknown',
      id: row.device_id,
      count: 0,
    }))
  } catch (e) {
    console.error('Error in fetchDeviceMappingList:', e)
    return []
  }
}

export const formatFilterDetails = (filterDetails) => {
  if (!filterDetails) return []
  if (!Array.isArray(filterDetails)) return filterDetails

  // Create a mapping of component names to actual components
  const componentMap = {
    IconTag,
    IconHeart,
    IconTruckDelivery,
    IconTemperature,
    IconClockExclamation,
    IconCalendarEvent,
    IconCalendarTime,
    IconCalendarPin,
    IconRoute,
    IconReplace,
    IconTruckLoading,
    IconMapPin,
    IconMapPinFilled,
    IconVaccineBottle,
    IconBarcode,
    IconBox,
    IconCubePlus,
    IconTruck,
    IconChecks,
    IconBoxOff,
    IconClockCheck,
    IconPlaneTilt,
    IconShip,
    CubeTick,
    IconUsers,
    IconZoomCheck,
    IconProgressCheck,
    IconHourglassHigh,
    IconUserSearch,
    IconCheck,
    IconCircleCheckFilled,
    IconCircleXFilled,
    IconAlertTriangleFilled,
    IconDeviceMobile,
  }

  // Create a mapping of image asset names to actual imported assets
  const imageMap = {
    ontrackIconGray,
    calenderMonthIcon,
    offtrackIcon,
    atriskIcon,
    ontrackIcon,
    TemperatureDeviationIcon,
  }

  // Function to create React component from iconData
  const createComponentFromIconData = (iconData) => {
    if (!iconData) return null

    if (iconData.type === 'component') {
      const Component = componentMap[iconData.name]
      if (!Component) {
        console.warn(`Component ${iconData.name} not found in componentMap`)
        return null
      }
      return React.createElement(Component, iconData.props || {})
    } else if (iconData.type === 'image') {
      // Handle both direct src paths and imported asset references
      let srcValue = iconData.src

      // If src is a string that matches an asset name, use the imported asset
      if (typeof iconData.src === 'string') {
        const assetName = Object.keys(imageMap).find(
          (key) => iconData.src.includes(key) || iconData.srcName === key,
        )
        if (assetName && imageMap[assetName]) {
          srcValue = imageMap[assetName]
        }
      }

      return React.createElement('img', {
        src: srcValue,
        alt: iconData.alt || '',
        className: iconData.className || '',
      })
    }

    return null
  }

  const formattedFilterDetails = filterDetails.map((filter) => {
    // Create a copy of the filter object
    const processedFilter = { ...filter }

    // Restore main level icons from iconData
    if (filter.iconData) {
      processedFilter.icon = createComponentFromIconData(filter.iconData)
    }

    // Restore subFilterIcon from subFilterIconData if it exists
    if (filter.subFilterIconData) {
      if (Array.isArray(filter.subFilterIconData)) {
        processedFilter.subFilterIcon = filter.subFilterIconData.map((iconData) =>
          createComponentFromIconData(iconData),
        )
      } else {
        processedFilter.subFilterIcon = createComponentFromIconData(filter.subFilterIconData)
      }
    }

    // Handle filterInstance if it exists
    if (filter.filterInstance) {
      const processedFilterInstance = {}

      // Iterate through each key in filterInstance (e.g., "Transport")
      Object.keys(filter.filterInstance).forEach((key) => {
        const instanceValue = filter.filterInstance[key]
        if (!instanceValue) {
          processedFilterInstance[key] = instanceValue
          return
        }
        const processedInstanceValue = { ...instanceValue }

        // Restore icon from iconData at the filterInstance level
        if (instanceValue.iconData) {
          processedInstanceValue.icon = createComponentFromIconData(instanceValue.iconData)
        }

        // Handle data array if it exists
        if (instanceValue.data && Array.isArray(instanceValue.data)) {
          processedInstanceValue.data = instanceValue.data.map((dataItem) => {
            const processedDataItem = { ...dataItem }

            // Restore icon from iconData at the data array level
            if (dataItem.iconData) {
              processedDataItem.icon = createComponentFromIconData(dataItem.iconData)
            }

            return processedDataItem
          })
        }

        processedFilterInstance[key] = processedInstanceValue
      })

      processedFilter.filterInstance = processedFilterInstance
    }

    // Handle direct data array if it exists (not nested in filterInstance)
    if (filter.data && Array.isArray(filter.data)) {
      processedFilter.data = filter.data.map((dataItem) => {
        const processedDataItem = { ...dataItem }

        // Restore icon from iconData at the data array level
        if (dataItem.iconData) {
          processedDataItem.icon = createComponentFromIconData(dataItem.iconData)
        }

        return processedDataItem
      })
    }

    return processedFilter
  })

  // console.log("formattedFilterDetails................", formattedFilterDetails);
  return formattedFilterDetails
}

export const formatDateToDDMMMYY = (timestamp) => {
  const date = new Date(timestamp)
  const day = date.getDate().toString().padStart(2, '0')
  const month = date.toLocaleString('en-US', { month: 'short' })
  const year = date.getFullYear().toString().substr(-2)
  return `${day}-${month} ${year}`
}

export const getTimezoneInfo = (timezone = null) => {
  const date = new Date('01-01-2025 9:00 AM')
  const targetTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: targetTimezone,
    timeZoneName: 'short',
    //   year: 'numeric',
    //   month: '2-digit',
    //   day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    //   second: '2-digit',
    hour12: true,
  })
  const parts = formatter.formatToParts(date)
  const timezoneName = parts.find((part) => part.type === 'timeZoneName')?.value || ''
  let fullTime = formatter.format(date)
  if (fullTime.includes('GMT+')) {
    const timeIndex = timezones.findIndex((t) => t.value === targetTimezone)
    if (timeIndex !== -1) {
      fullTime = fullTime.split(' ')
      fullTime[2] = timezones[timeIndex].code
      fullTime = fullTime.join(' ')
    }
  }
  return {
    timezone: targetTimezone,
    abbreviation: timezoneName,
    fullTime,
    timestamp: date.getTime(),
  }
}

export const numberFormatToFixedDecimal = (number, decimalPlaces = 2) => {
  // Handle edge cases: null, undefined, empty string
  if (number === null || number === undefined || number === '') {
    return '--'
  }

  // Convert to number if it's a string
  const numValue = typeof number === 'string' ? Number.parseFloat(number) : Number(number)

  // Validate that we have a valid number (handles NaN, Infinity)
  if (Number.isNaN(numValue) || !isFinite(numValue)) {
    return '--'
  }
  const options = {
    // We can use a high minimumFractionDigits if needed, but max is key here
    maximumFractionDigits: decimalPlaces,
    // Use the 'en-US' locale for consistent results (adjust as needed for other locales)
  }

  if (numValue?.toString()?.includes('.') && numValue?.toString()?.split('.')[1]?.length === 1) {
    return numValue.toFixed(decimalPlaces)
  }

  // Create the formatter instance and format the number
  return new Intl.NumberFormat('en-US', options).format(numValue)
}

/**
 * Formats a numeric value for display with exactly 2 decimal places.
 */
export const formatNumericToTwoDecimals = (value) => {
  if (value === null || value === undefined || value === '') {
    return ''
  }
  const num = typeof value === 'number' ? value : Number.parseFloat(String(value).trim())
  if (isNaN(num)) {
    return typeof value === 'string' ? value : String(value)
  }
  const rounded = Math.round(num * 100 + 1e-10) / 100
  return rounded.toFixed(2)
}

/**
 * Rounds a float to 2 decimal places and returns a number.
 */
export const roundFloatToTwoDecimals = (value) => {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const floatValue = typeof value === 'number' ? value : Number.parseFloat(value)
  if (isNaN(floatValue)) {
    return null
  }
  return Math.round(floatValue * 100 + 1e-10) / 100
}

export const formatNumericToTwoDecimalsForDisplay = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-'
  }
  const num = typeof value === 'number' ? value : Number.parseFloat(String(value).trim())
  if (isNaN(num)) {
    return typeof value === 'string' ? value : String(value)
  }
  const rounded = Math.round(num * 100 + 1e-10) / 100
  return rounded.toFixed(2)
}

export const fetchUserGroupSubscribers = async (topicKeys, session) => {
  try {
    if (!session) return []

    const client = createClerkSupabaseClientCommandCenter(session)

    const { data, error } = await client
      .from('user_access_groups')
      .select('subscribers')
      .in('topic_key', topicKeys)

    if (error) {
      console.error('Error fetching user groups:', error)
      return []
    }

    const allSubscribers = data?.flatMap((row) => row.subscribers || []) || []
    const uniqueSubscribers = [...new Set(allSubscribers)]

    return uniqueSubscribers
  } catch (error) {
    console.error('Error fetching user groups:', error)
    return []
  }
}

export const fetchUserGroupSubscribersCanReview = async (topicKeys, session) => {
  try {
    if (!session) return []

    const client = createClerkSupabaseClientCommandCenter(session)

    const { data, error } = await client
      .from('user_access_groups')
      .select('subscribers')
      .in('topic_key', topicKeys)
      .eq('features->temprelease_reviewer', true)

    if (error) {
      console.error('Error fetching user groups with reviewer access:', error)
      return []
    }

    const allSubscribers = data?.flatMap((row) => row.subscribers || []) || []
    const uniqueSubscribers = [...new Set(allSubscribers)]

    return uniqueSubscribers
  } catch (error) {
    console.error('Error fetching user groups with reviewer access:', error)
    return []
  }
}

export const fetchUserGroupSubscribersEmailSignatures = async (topicKeys, session) => {
  try {
    if (!session) return []

    const client = createClerkSupabaseClientCommandCenter(session)

    const { data, error } = await client
      .from('user_access_groups')
      .select('subscribers')
      .in('topic_key', topicKeys)
      .eq('features->email_signature', true)

    if (error) {
      console.error('Error fetching user groups with email signatures access:', error)
      return []
    }

    const allSubscribers = data?.flatMap((row) => row.subscribers || []) || []
    const uniqueSubscribers = [...new Set(allSubscribers)]

    return uniqueSubscribers
  } catch (error) {
    console.error('Error fetching user groups with email signatures access:', error)
    return []
  }
}

const getCurrentOffset = (timeZone) => {
  const now = new Date()
  const utcTime = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }))
  const localTime = new Date(now.toLocaleString('en-US', { timeZone }))
  const offsetInMinutes = (localTime.getTime() - utcTime.getTime()) / (1000 * 60)
  return offsetInMinutes / 60 // Convert to hours
}

export const getTimezoneAbbreviation = (timeZone) => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    })
    const parts = formatter.formatToParts(new Date())

    if (getCurrentOffset(timeZone) > 0) {
      return TimeZones?.filter((tz) => tz.value === timeZone)[0]?.code || 'UTC'
    }
    return parts.find((part) => part.type === 'timeZoneName')?.value || ''
  } catch (e) {
    console.error(e)
    return ''
  }
}

export const capitalizeWords = (str) => {
  if (!str || typeof str !== 'string') return str
  // Preserve special cases
  if (
    str === 'NA' ||
    str === 'na' ||
    str === 'Na' ||
    str === 'nA' ||
    str === 'N/A' ||
    str === 'n/a' ||
    str === 'N/a' ||
    str === 'n/A'
  )
    return 'NA'

  return str
    .split('\n')
    .map((line) =>
      line
        .split(' ')
        .map((word) => {
          if (!word) return word

          const cleanWord = word.replace(/[()]/g, '')
          const upperWord = cleanWord.toUpperCase()

          // Rule 6: Do not change alphanumeric IDs
          if (/^[A-Z0-9]+$/i.test(cleanWord) && /\d/.test(cleanWord)) {
            return word
          }

          // Rule 7: NA variations
          if (
            ['NA', 'N/A', 'N\\A'].includes(upperWord) ||
            ['na', 'n/a', 'aN', 'Na', 'n/A', 'N/a'].includes(cleanWord)
          ) {
            return word.replace(cleanWord, upperWord === 'NA' ? 'NA' : 'N/A')
          }

          // Rule 1 & 5: AM / PM
          if (upperWord === 'AM' || upperWord === 'PM') {
            return word.replace(cleanWord, upperWord)
          }

          // Rule 4: (example) → (Example)
          if (/^\(.*\)$/.test(word)) {
            const inner = cleanWord.charAt(0).toUpperCase() + cleanWord.slice(1).toLowerCase()
            return `(${inner})`
          }

          // Rule 2 & 3: Capitalize each word
          return cleanWord.charAt(0).toUpperCase() + cleanWord.slice(1).toLowerCase()
        })
        .join(' '),
    )
    .join('\n')
}

export const convertSTRToIdealRange = (input) => {
  if (!input) return input
  if (typeof input === 'string') {
    return input
      .replace(/STR Range/gi, 'Ideal Range')
      .replace(/STR/gi, 'Ideal Range')
      .replace(/Str/gi, 'Ideal Range')
      .replace(/str/gi, 'Ideal Range')
      .replace(/str range/gi, 'Ideal Range')
      .replace(/Str Range/gi, 'Ideal Range')
  } else {
    return input
  }
}

export const IlikePatternForSearch = (raw) => {
  const normalised = raw.trim().replace(/\s+/g, ' ')
  const escaped = normalised.replace(/[%_]/g, (c) => `\\${c}`).replace(/"/g, '""')
  return `"*${escaped}*"`
}

export const userOrganizationPermissions = (organizationMemberships) => {
  const orgId = window?.Clerk?.organization?.id
  const userPermissions = organizationMemberships?.find(
    (membership) => membership?.organization?.id === orgId,
  )?.permissions
  return userPermissions
}

export const userOrganizationRole = (organizationMemberships) => {
  const orgId = window?.Clerk?.organization?.id
  const userRole = organizationMemberships?.find(
    (membership) => membership?.organization?.id === orgId,
  )?.role
  return userRole
}

export const decryptKey = async (
  encryptedData,
  ivBase64,
  saltKey = 'static-salt',
  secretKey = import.meta.env.VITE_SALT_KEY,
) => {
  const encoder = new TextEncoder()
  const iv = Uint8Array.from(atob(ivBase64), (c) => c.charCodeAt(0))
  const data = Uint8Array.from(atob(encryptedData), (c) => c.charCodeAt(0))

  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(secretKey),
    'PBKDF2',
    false,
    ['deriveKey'],
  )
  const key = await window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode(saltKey),
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt'],
  )

  const decrypted = await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data)
  return new TextDecoder().decode(decrypted)
}

export const fetchPassiveDataFiles = async (session, shipmentUuid, deviceId) => {
  const emptyResult = deviceId ? [] : {}

  if (!session || !shipmentUuid) return emptyResult

  try {
    const parserClientEdge = createClerkSupabaseClientParserEdge(session)

    const payload = { shipment_id: shipmentUuid }
    if (deviceId) {
      payload.device_id = deviceId
    }

    const { data, error } = await parserClientEdge.functions.invoke(
      resolveEdgeFromApiVersions('get-passive-data-files'),
      { body: payload },
    )

    if (error) {
      console.error('Error fetching passive data files:', error)
      return emptyResult
    }

    if (deviceId) {
      if (Array.isArray(data)) return data
      if (Array.isArray(data?.data)) return data.data
      if (Array.isArray(data?.files)) return data.files
      return []
    }

    if (data && typeof data === 'object' && !Array.isArray(data)) {
      if (data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
        return data.data
      }
      if (data.files && typeof data.files === 'object' && !Array.isArray(data.files)) {
        return data.files
      }
      return data
    }
    return {}
  } catch (error) {
    console.error('Error fetching passive data files:', error)
    return emptyResult
  }
}

export const validateSessionAndRetry = (channel, status, session, callbackFn) => {
  if (!session || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
    callbackFn()
  }
}

export function shouldShowProductStabilitySection({ isExternalShare, productName }) {
  const externalShareAllowed = isExternalShare ? isExternalShare === 'external/track-product' : true
  if (!externalShareAllowed) {
    return false
  }

  return Boolean(productName)
}

export function getQualifyingProductTemperatureStatus({
  shipmentTemperatureStatus,
  deviceTemperatureDevices,
}) {
  if (shipmentTemperatureStatus != null && shipmentTemperatureStatus !== '') {
    return shipmentTemperatureStatus
  }

  const devicesWithStatus = deviceTemperatureDevices?.filter(
    (device) =>
      device?.temperature_status != null && device?.temperature_status !== '' && !device?.deleted,
  )

  if (!devicesWithStatus?.length) {
    return null
  }

  const primaryDevice = devicesWithStatus.find((device) => device.primary)
  return primaryDevice?.temperature_status ?? devicesWithStatus[0].temperature_status
}

export function shouldShowStabilityBudgetBar({ productStability, temperatureStatus }) {
  const hasStabilityValue = productStability !== null && productStability !== undefined
  if (!hasStabilityValue) {
    return false
  }

  return temperatureStatus != null
}

export function shouldShowStabilityBudgetSection({ productStability, temperatureStatus }) {
  return shouldShowStabilityBudgetBar({
    productStability,
    temperatureStatus,
  })
}

export const PELI_BIOTHERMAL_WHITE_LABEL_TENANT_GATE =
  'cc-6590-create-peli-biothermal-white-label-tenant'

export const getFeaturePermissionsStorage = (uagEnhancements) => {
  try {
    const raw = uagEnhancements
      ? localStorage.getItem('userFeaturePermissions')
      : localStorage.getItem('featurePermissions')
    if (!raw) {
      return null
    }
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export const checkUserFeatureAccess = (featureName, accessType) => {
  const permissions = JSON.parse(localStorage.getItem('userFeaturePermissions'))
  if (!permissions) {
    return false
  }
  if (Array.isArray(accessType)) {
    return accessType.some((type) => permissions[featureName]?.[type] || false)
  }
  return permissions?.[featureName]?.[accessType] || false
}

export const checkAutomationWorkflowsViewAccess = (
  uagEnhancements,
  { hasFetchedPermissions, featurePermissions } = {},
) => {
  if (uagEnhancements) {
    return checkUserFeatureAccess('automation_workflows', ['read', 'manage'])
  }
  return Boolean(
    hasFetchedPermissions?.['automation-workflows'] ?? featurePermissions?.['automation-workflows'],
  )
}

export const checkAutomationWorkflowsManageAccess = (
  uagEnhancements,
  { organizationMemberships, hasAuth } = {},
) => {
  if (uagEnhancements) {
    return checkUserFeatureAccess('automation_workflows', 'manage')
  }
  if (typeof hasAuth === 'function') {
    return hasAuth({ permission: 'org:automation_workflow:manage' })
  }
  return userOrganizationPermissions(organizationMemberships)?.includes(
    'org:automation_workflow:manage',
  )
}

export const isTempReleaseShipmentManagePath = (statsigClient) => {
  const pathParts = window.location.pathname.split('/').map((part) => part.toLowerCase())
  if (
    isProductReleasePathPermissionCheckEnabled(statsigClient) &&
    pathParts.includes('product-release')
  ) {
    return true
  }
  return pathParts.includes('temperature')
}

export const checkOrgFeatureAccess = (featureName) => {
  const permissions = JSON.parse(localStorage.getItem('orgFeaturePermissions'))
  if (!permissions) {
    return false
  }
  return permissions?.[featureName] || false
}

export const getUserFeaturePermissionTags = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem('userFeaturePermissionsTags') || 'null')
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    return parsed
  } catch {
    return {}
  }
}

const hasTagScopedShipmentAccess = (featurePerms, permissionKey, shipmentTags) => {
  if (!featurePerms) return false
  if (featurePerms.all_tags) return true

  const allowed = featurePerms[permissionKey]
  if (allowed == null) return true
  if (!Array.isArray(allowed) || !allowed.length) return false

  const shipmentTagIds = (Array.isArray(shipmentTags) ? shipmentTags : [])
    .map((tag) => {
      if (typeof tag === 'string' || typeof tag === 'number') {
        return String(tag).trim()
      }
      return String(tag?.tag_id || tag?.id || '').trim()
    })
    .filter(Boolean)
  if (!shipmentTagIds.length) return false

  const allowedSet = new Set(allowed.map((id) => String(id).trim()).filter(Boolean))
  return shipmentTagIds.some((id) => allowedSet.has(id))
}

export const checkShipmentEditAccess = (shipmentType, shipmentTags, permissionTags) =>
  hasTagScopedShipmentAccess(permissionTags?.[shipmentType], 'manage', shipmentTags)

export const checkShipmentReviewAccess = (shipmentTags, permissionTags) =>
  hasTagScopedShipmentAccess(permissionTags?.temprelease, 'can_review', shipmentTags)

export const checkShipmentCreateManageAccess = (shipmentType) => {
  const featureName =
    shipmentType ?? (isTempReleaseShipmentManagePath() ? 'temprelease' : 'commandcenter')
  return checkUserFeatureAccess(featureName, 'manage')
}

export const checkShipmentEditFeatureAccess = (shipmentTags, { shipmentType } = {}) => {
  const featureName =
    shipmentType ?? (isTempReleaseShipmentManagePath() ? 'temprelease' : 'commandcenter')
  if (!checkUserFeatureAccess(featureName, 'manage')) return false
  return checkShipmentEditAccess(featureName, shipmentTags, getUserFeaturePermissionTags())
}

export const checkShipmentReviewFeatureAccess = (shipmentTags) => {
  if (!checkUserFeatureAccess('temprelease', 'can_review')) return false
  if (shipmentTags == null) return false
  return checkShipmentReviewAccess(shipmentTags, getUserFeaturePermissionTags())
}

export const checkBackendOnlyFeatureAccess = (featureName) => {
  const permissions = JSON.parse(localStorage.getItem('backendOnlyFeaturePermissions'))
  if (!permissions) {
    return false
  }
  return permissions?.[featureName] || false
}

export const checkTenantFeatureAccess = (featureName) => {
  const permissions = JSON.parse(localStorage.getItem('tenantFeaturePermissions'))
  if (!permissions) {
    return false
  }
  return permissions?.[featureName] || false
}

export const getUserUagName = () => {
  return JSON.parse(localStorage.getItem('userUagName'))?.toLowerCase() || ''
}

export const isTenantOwner = () => {
  return JSON.parse(localStorage.getItem('isTenantOwner')) || false
}

/**
 * @param {string} orgId
 * @param {string|undefined|null} userEnterpriseConnectionId
 * @param {object} tenantEnterpriseConnectionIds
 * @returns {string|boolean}
 */
export function getAllowedTenantEnterpriseType(
  orgId,
  userEnterpriseConnectionId,
  tenantEnterpriseConnectionIds,
) {
  const normalizedUserConnectionId = String(userEnterpriseConnectionId || '').trim()
  if (!normalizedUserConnectionId) {
    return false
  }
  const tenantConnectionId =
    tenantEnterpriseConnectionIds[orgId] || tenantEnterpriseConnectionIds.all
  return tenantConnectionId &&
    normalizedUserConnectionId === tenantConnectionId?.id &&
    tenantConnectionId?.type
    ? tenantConnectionId?.type
    : false
}

export function isUserNotFoundMetadataError(error) {
  const status = error?.status
  const message = String(error?.message || '').toLowerCase()
  return status === 404 || message.includes('user_not_found') || message.includes('no user found')
}

export function extractPublicMetadataFromContxtUser(userResponse) {
  if (!userResponse || typeof userResponse !== 'object') {
    return {}
  }

  return userResponse.public_metadata ?? userResponse.publicMetadata ?? {}
}

export const getUserPublicMetadataClerk = async (clerkToken, userId, options = {}) => {
  const tenantId = getTenantIdFromContext()
  const apiKey = await resolveContxtBackendApiKey({
    landingZoneClient: options.landingZoneClient,
    commandCenterClient: options.commandCenterClient,
    tenantId,
    useTenantBackendApiKey: options.useTenantBackendApiKey,
  })

  const response = await fetch(`${import.meta.env.VITE_CONTXT_API_URL}/app/users/${userId}`, {
    method: 'GET',
    headers: {
      'Api-Key': apiKey,
      'X-Clerk-Token': clerkToken,
      ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
    },
  })

  const data = await response.json()

  if (!response.ok) {
    const error = new Error(data?.message || `Failed to fetch user (${response.status})`)
    error.status = response.status
    throw error
  }

  return data ?? {}
}

export const updateUserPublicMetaDataClerk = async (
  clerkToken,
  userId,
  userPublicMetaData,
  options = {},
) => {
  const tenantId = getTenantIdFromContext()
  const apiKey = await resolveContxtBackendApiKey({
    landingZoneClient: options.landingZoneClient,
    commandCenterClient: options.commandCenterClient,
    tenantId,
    useTenantBackendApiKey: options.useTenantBackendApiKey,
  })

  const response = await fetch(`${import.meta.env.VITE_CONTXT_API_URL}/app/users/${userId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Api-Key': apiKey,
      'X-Clerk-Token': clerkToken,
      ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
    },
    body: JSON.stringify({
      contract_version: '1.0',
      public_metadata: userPublicMetaData,
    }),
  })

  const data = await response.json()
  return data
}

export const fetchUserDetailsClerk = async (clerkToken, userId, options = {}) => {
  const tenantId = getTenantIdFromContext()
  const apiKey = await resolveContxtBackendApiKey({
    landingZoneClient: options.landingZoneClient,
    commandCenterClient: options.commandCenterClient,
    tenantId,
    useTenantBackendApiKey: options.useTenantBackendApiKey,
  })

  const response = await fetch(`${import.meta.env.VITE_CONTXT_API_URL}/app/users/${userId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Api-Key': apiKey,
      'X-Clerk-Token': clerkToken,
      ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
    },
  })

  const data = await response.json()

  if (!response.ok) {
    const error = new Error(data?.message || data?.error || 'Error fetching user details')
    error.status = response.status
    error.data = data
    throw error
  }

  return data
}

export const updateUserDetailsClerk = async (clerkToken, userId, payload, options = {}) => {
  const tenantId = getTenantIdFromContext()
  const apiKey = await resolveContxtBackendApiKey({
    landingZoneClient: options.landingZoneClient,
    commandCenterClient: options.commandCenterClient,
    tenantId,
    useTenantBackendApiKey: options.useTenantBackendApiKey,
  })

  const response = await fetch(`${import.meta.env.VITE_CONTXT_API_URL}/app/users/${userId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Api-Key': apiKey,
      'X-Clerk-Token': clerkToken,
      ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
    },
    body: JSON.stringify({
      contract_version: '1.0',
      ...payload,
    }),
  })

  const data = await response.json()

  if (!response.ok) {
    const error = new Error(data?.message || data?.error || 'Error updating user details')
    error.status = response.status
    error.data = data
    throw error
  }

  return data
}
